/**
 * 상담사 번호 모달의 내선 선택·상태 표시에 쓰는 순수 판정.
 *
 * 내선은 4단계부터 서버가 발급·회수한다. studio 는 번호를 만들지 않고, 서버가 준
 * 목록에서 고르거나 자동 발급을 요청할 뿐이다. 여기 모인 규칙은 전부 서버 계약을
 * 그대로 옮긴 것이라 화면 밖에서 따로 검증할 수 있게 뷰에서 떼어 둔다.
 * 계약: ~/md/front/studio-콜봇-내선-자동발급-요청.md
 *
 * admin 이식: studio `src/utils/agent/pbxExtensions.ts` 를 타입만 걷어 내고 그대로 옮겼다
 * (~/md/front/admin-콜봇-내선관리-화면-요청.md). 끝의 `formatTelephoneNumber`·`formatFileSize` 는
 * studio 의 다른 유틸에서 쓰던 것을 이 화면에 필요한 만큼만 여기로 모은 것이다.
 * 브라우저 전용 API(window·document)를 쓰지 않는다 — node:test 로 검증한다.
 */
import dayjs from 'dayjs';

/**
 * 드롭다운에 보일 내선 — 서버 소유 검사가 받는 상태(active·legacy)이면서 아직
 * 어느 행에도 붙지 않은 것. 수정 중인 행에 이미 붙은 내선은 그 행에서만 다시 고를
 * 수 있어야 하므로 함께 남긴다(한 내선은 한 행에만 붙는다).
 */
export function selectableExtensions(items, editingNumberId = '') {
  return items.filter(
    (e) =>
      (e.status === 'active' || e.status === 'legacy') &&
      (e.counselor_number_id === null ||
        (!!editingNumberId && e.counselor_number_id === editingNumberId))
  );
}

/** 아직 어느 행에도 붙지 않은 내선이 하나라도 있는가 — 모달 기본값을 정한다 */
export function hasFreeExtension(items) {
  return items.some(
    (e) => (e.status === 'active' || e.status === 'legacy') && e.counselor_number_id === null
  );
}

/**
 * 드롭다운 라벨. 번호 뒤에 전화기 등록 상태를 단다 — 사용자가 고르는 기준은
 * "지금 쓰는 전화기가 붙어 있는 내선인가" 하나뿐이다. 출처(발급/인수/레거시)는 운영자
 * 개념이라 화면에 내지 않는다. 문구는 목록 배지(registrationBadge)와 같은 말을 쓴다.
 */
export function extensionOptionLabel(e) {
  return `${e.number} · ${registrationBadge(e.registration?.status ?? null).label}`;
}

/**
 * 목록 배지 — PBX 반영 상태. synced·n/a 는 표시하지 않는다(정상이거나 반영 대상이
 * 아니다). pending·partial 은 서버가 재시도 중, error 는 사람이 조치해야 한다.
 */
export function syncStateBadge(state) {
  if (state === 'pending' || state === 'partial') return { label: 'PBX 반영 중', tone: 'pending' };
  if (state === 'error') return { label: 'PBX 반영 실패', tone: 'error' };
  return null;
}

/**
 * 목록 배지·드롭다운 라벨 — 전화기 등록 상태를 사용자 말로.
 *
 * 서버는 네 상태(reachable / unreachable / unregistered / unknown·null)를 구분하지만
 * 사용자에게 필요한 건 "전화기가 붙어 있나" 하나다. unregistered(관측했더니 없음)와
 * null(관측한 적 없음)은 서버엔 다른 상태여도 사용자에겐 둘 다 "없음"이다.
 * "확인 중" 을 두지 않는 이유: 전화기가 연결되지 않은 내선에선 끝나는 조건이 없는 확인이
 * 된다. 연결 안 된 내선은 이 3단 문구, 전화기가 연결돼 설정을 받는 중인 내선은 파생
 * `설정 적용 중` 을 쓴다 — 판정은 provisioningChip().
 */
export function registrationBadge(status) {
  switch (status) {
    case 'reachable':
      return { label: '전화기 연결됨', tone: 'ok' };
    case 'unreachable':
      return { label: '전화기 응답 없음', tone: 'warn' };
    default:
      return { label: '전화기 없음', tone: 'muted' };
  }
}

/**
 * 조작 응답의 warnings → 토스트 문구와 종류. ERROR 가 하나라도 있으면 그것이 우선이다 —
 * 재시도 안내를 보여 주면 사용자가 기다리기만 하고 아무도 조치하지 않는다.
 * warnings 가 없으면 null(정상 성공 토스트). `done` 은 문구 앞부분 — 상담사 번호 생성은
 * `상담사 번호를 추가했습니다`, 내선 관리 조작은 `요청을 접수했습니다`.
 */
export function createWarningToast(warnings, { done } = { done: '상담사 번호를 추가했습니다' }) {
  if (!warnings?.length) return null;
  if (warnings.some((w) => w.code === 'PBX_EXTENSION_SYNC_ERROR')) {
    return {
      kind: 'error',
      text: `${done}만 PBX 반영에 실패했습니다. 담당자에게 알려 주세요.`,
    };
  }
  return {
    kind: 'warning',
    text: `${done}. PBX 반영이 끝나지 않아 서버가 자동으로 다시 시도합니다.`,
  };
}

/**
 * 저장 전에 회수 경고를 띄워야 하는가. 연결된 내선 행에서 번호를 다른 내선으로
 * 바꾸거나 내선 사용을 끄면 서버가 기존 회선을 PBX 에서 즉시 지운다 — 되돌릴 수 없다.
 * 이름·착신전환·링그룹만 바꾸는 저장은 해당 없다.
 */
export function revokesLinkedExtension(linked, next) {
  if (!linked) return false;
  if (!next.isExtension) return true;
  return next.number !== linked.number;
}

// ---------------------------------------------------------------------------
// 내선 관리 탭(7단계) — 상태 라벨·파생 판정·버튼 활성 규칙.
// 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "상태·라벨 매핑"
// ---------------------------------------------------------------------------

/**
 * @typedef {'ok' | 'warn' | 'error' | 'pending' | 'muted'} PbxBadgeTone
 * @typedef {{ label: string, tone: PbxBadgeTone, title?: string }} PbxBadge  title 은 툴팁 — 없으면 생략
 */

/** 내선 상태 배지 */
export function extensionStatusBadge(status) {
  switch (status) {
    case 'active':
      return { label: '사용 중', tone: 'ok' };
    case 'disabled':
      return { label: '일시 중지', tone: 'muted' };
    case 'locked':
      return { label: '잠김', tone: 'error' };
    case 'retired':
      return { label: '회수됨', tone: 'muted' };
    case 'blocked':
      return { label: '차단됨', tone: 'error' };
    case 'legacy':
      return {
        label: '정적',
        tone: 'muted',
        title: 'PBX 에 직접 정의된 회선이라 call-api 가 반영하지 않습니다',
      };
    default:
      return { label: String(status), tone: 'muted' };
  }
}

/**
 * 내선 출처 — 운영자 화면(내선 관리)에서만 쓴다. 상담사 번호 모달은 출처를 내지 않는다
 * (병원 사용자에겐 운영자 개념 — extensionOptionLabel 주석).
 */
export function extensionOriginLabel(origin) {
  if (origin === 'issued') return '자동 발급';
  if (origin === 'adopted') return '가져옴';
  if (origin === 'legacy') return '정적';
  return String(origin);
}

export const EXTENSION_ORIGIN_HINT =
  '정적 = PBX 에 직접 정의된 회선, call-api 가 반영하지 않음(sync n/a)';

const ROTATION_REASON_LABELS = {
  manual: '수동',
  unlock: '잠금 해제',
  device_replace: '전화기 교체',
  device_unassign: '연결 해제',
};

export function rotationReasonLabel(reason) {
  if (!reason) return '';
  return ROTATION_REASON_LABELS[reason] ?? reason;
}

/** `전화기 대기` 칩 툴팁 — 설정 재수신을 어떻게 보냈는지(notify.outcome)로 나눈다 */
export function awaitingPhoneHint(outcome) {
  switch (outcome) {
    case 'queued':
      return '설정 재수신을 보냈습니다. 전화기 응답 대기';
    case 'no_contact':
      return '전화기가 등록돼 있지 않아 다음 설정 수신 때 받아 갑니다';
    case 'in_call':
      return '통화 중이라 보내지 못했습니다. 다음 설정 수신 때 받아 갑니다';
    case 'unavailable':
    case 'failed':
      return '설정 재수신을 보내지 못했습니다. 다음 설정 수신 때 받아 갑니다';
    default:
      return '전화기가 새 자격증명을 아직 받지 않음(설정 수신 대기)';
  }
}

/**
 * 시크릿 회전 칩. rotation 이 null 이면 빈 칸. `완료` 는 다음 재조회에서 rotation 이
 * null 이 되며 저절로 사라진다. `실패` 는 전화기가 이전 비밀번호로 동작 중이라는 뜻이다.
 */
export function rotationChip(rotation) {
  if (!rotation) return null;
  const reason = rotationReasonLabel(rotation.reason);
  const suffix = reason ? ` (사유: ${reason})` : '';
  switch (rotation.state) {
    case 'pending':
    case 'put_confirmed':
      return { label: '반영 중', tone: 'pending', title: `새 SIP 비밀번호를 PBX 에 반영하는 중입니다${suffix}` };
    case 'awaiting_phone':
      return { label: '전화기 대기', tone: 'warn', title: awaitingPhoneHint(rotation.notify?.outcome) + suffix };
    case 'confirmed':
      return { label: '완료', tone: 'ok', title: `시크릿 회전을 마쳤습니다${suffix}` };
    case 'failed':
      return {
        label: '실패',
        tone: 'error',
        title:
          'PBX 반영에 실패했습니다. 전화기는 이전 비밀번호로 동작 중입니다. [시크릿 회전]을 다시 누르면 재시도합니다.',
      };
    default:
      return null;
  }
}

/** 회전이 아직 진행 중인가 — 진행 중이면 재회전을 막는다(서버도 409) */
export function isRotationInProgress(rotation) {
  return !!rotation && rotation.state !== 'confirmed' && rotation.state !== 'failed';
}

/**
 * "설정 적용 중" 창 — 전화기를 연결했거나 설정을 받아 간 뒤 이 시간 안에 등록이 안 보이면
 * 아직 적용 중으로 본다. 결정 시트에 값이 없다(열린 질문): 폴링 주기가 실측으로 정해지면
 * 그 주기 + 등록 만료(120초)로 바꾼다. 요청 문서 "사용자 결정 필요" 2.
 */
export const PROVISIONING_APPLYING_WINDOW_MS = 10 * 60 * 1000;

/**
 * 파생 칩 `설정 적용 중` — 전화기가 연결됐고, 최근(창 안에) 설정을 받아 갔거나 막 연결됐는데,
 * 등록이 아직 reachable 이 아닐 때. 연결 직후 첫 설정 수신 전 공백을 메우려고
 * `last_provision.at` 이 없으면 `assigned_at` 을 쓴다(결정 1·19).
 * 전화기 목록이 없는 화면(상담사 번호 탭)에서는 쓰지 않는다.
 */
export function provisioningChip(ext, device, now, windowMs = PROVISIONING_APPLYING_WINDOW_MS) {
  if (!ext.device_id || !device) return null;
  if (ext.registration?.status === 'reachable') return null;
  const since = device.last_provision?.at ?? device.assigned_at;
  if (since == null || now - since > windowMs) return null;
  return {
    label: '설정 적용 중',
    tone: 'pending',
    title: '전화기가 새 설정을 받아 등록하는 중입니다',
  };
}

/** 내선 탭 "전화기(등록)" 열 — 파생 칩이 있으면 그것, 없으면 4단계 3단 문구 */
export function phoneRegistrationBadge(ext, device, now) {
  return provisioningChip(ext, device, now) ?? registrationBadge(ext.registration?.status ?? null);
}

export function provisioningStateBadge(state) {
  if (state === 'current') return { label: '최신', tone: 'ok' };
  if (state === 'stale') return { label: '갱신 필요', tone: 'warn' };
  return { label: '미수신', tone: 'muted' };
}

export function deviceStatusBadge(status) {
  if (status === 'assigned') return { label: '연결됨', tone: 'ok' };
  if (status === 'returned') return { label: '반납됨', tone: 'muted' };
  return { label: '미배정', tone: 'muted' };
}

export function siteStatusBadge(status) {
  return status === 'active' ? { label: '사용 중', tone: 'ok' } : { label: '폐기됨', tone: 'muted' };
}

/** 전송 기본 포트 — 서버도 포트 생략 시 이 값을 쓴다 */
export function defaultSipPort(transport) {
  return transport === 'tls' ? 5061 : 5060;
}

/**
 * 전화기 전송 표시. null 은 설치 장소 기본값을 따른다는 뜻 — 설치 장소를 알면 `설치 장소 기본값(TLS 5061)`.
 * UDP 는 통화 키가 평문으로 오가 warn 톤.
 */
export function transportBadge(transport, port = null, siteDefault) {
  if (!transport) {
    if (!siteDefault) return { label: '설치 장소 기본값', tone: 'muted' };
    const inner = transportBadge(siteDefault.transport, siteDefault.port);
    return { label: `설치 장소 기본값(${inner.label})`, tone: inner.tone };
  }
  const p = port ?? defaultSipPort(transport);
  return transport === 'udp'
    ? { label: `UDP ${p}`, tone: 'warn', title: 'UDP 는 통화 키가 평문으로 오갑니다 — 임시 롤백 용도' }
    : { label: `TLS ${p}`, tone: 'ok' };
}

/** 번호 해제(공용 풀 복귀)는 STUDIO·RESELLER 만(결정 6). 강제는 서버 */
export function canReleasePbxExtension(role) {
  return role === 'STUDIO' || role === 'RESELLER';
}

/**
 * @typedef {'sync' | 'rotate' | 'checkSync' | 'disable' | 'resume' | 'lock' | 'unlock' | 'release'} ExtensionActionKey
 * @typedef {{ enabled: boolean, reason?: string }} ExtensionActionState  reason 은 비활성 이유 툴팁
 */

export const EXTENSION_UNLINKED_HINT = '상담사 번호에 연결된 내선만 조작할 수 있습니다';
export const STAGE7_UNAVAILABLE_HINT = '이 환경의 call-api 에는 아직 내선 관리(7단계)가 배포되지 않았습니다.';
/** 7단계 판정 프로브(GET pbx-sites)가 미구현이 아닌 이유로 실패 — 미배포라고 단정하지 않는다 */
export const STAGE7_PROBE_FAILED_HINT = '내선 관리 기능 확인에 실패했습니다. 다시 시도해 주세요.';

/**
 * 행 상태별 작업 버튼 활성. `stage7` 은 이 환경에 7단계 라우트가 있는가 — 없으면 4단계
 * 라우트(재동기화·잠금)만 쓸 수 있다. null 은 아직 모름(판정 중 또는 프로브 실패)이라
 * 7단계 작업은 막되, 미배포라고 말하지 않는다 — 프로브 실패(`stage7ProbeFailed`)일 때만
 * 다시 시도 안내를 단다. 번호 해제만 내선 id 로 부르고 나머지는 상담사 번호 id 로
 * 부르므로, 연결 안 된 내선은 해제를 뺀 전부가 비활성이다.
 * @returns {Record<ExtensionActionKey, ExtensionActionState>}
 */
export function extensionActionStates(ext, opts) {
  const linked = !!ext.counselor_number_id;
  const s = ext.status;
  const stage7Off = {
    enabled: false,
    reason:
      opts.stage7 === false ? STAGE7_UNAVAILABLE_HINT : opts.stage7ProbeFailed ? STAGE7_PROBE_FAILED_HINT : undefined,
  };
  const gate = (ok, needs7, statusHint) => {
    if (needs7 && opts.stage7 !== true) return stage7Off;
    if (!linked) return { enabled: false, reason: EXTENSION_UNLINKED_HINT };
    return ok ? { enabled: true } : { enabled: false, reason: statusHint };
  };
  const rotating = isRotationInProgress(ext.rotation);
  let release;
  if (opts.stage7 !== true) release = stage7Off;
  else if (!opts.canRelease) release = { enabled: false, reason: '번호 해제는 스튜디오·리셀러 계정만 할 수 있습니다' };
  else if (s !== 'retired' && s !== 'blocked')
    release = { enabled: false, reason: '회수됐거나 차단된 내선만 해제할 수 있습니다' };
  else release = { enabled: true };
  return {
    sync: gate(
      s === 'active' && ext.sync_state !== 'n/a',
      false,
      ext.sync_state === 'n/a' ? '정적 회선은 PBX 반영 대상이 아닙니다' : '사용 중인 내선만 재동기화할 수 있습니다'
    ),
    rotate: gate(
      s === 'active' && !rotating,
      true,
      rotating ? '진행 중인 회전이 끝난 뒤 다시 할 수 있습니다' : '사용 중인 내선만 회전할 수 있습니다'
    ),
    checkSync: gate(
      s === 'active' && !!ext.device_id,
      true,
      s === 'active' ? '전화기가 연결된 내선만 설정을 다시 보낼 수 있습니다' : '사용 중인 내선만 설정을 다시 보낼 수 있습니다'
    ),
    disable: gate(s === 'active', true, '사용 중인 내선만 일시 중지할 수 있습니다'),
    resume: gate(s === 'disabled', true, '일시 중지된 내선만 재개할 수 있습니다'),
    lock: gate(s === 'active' || s === 'disabled', false, '사용 중이거나 일시 중지된 내선만 잠글 수 있습니다'),
    unlock: gate(s === 'locked', true, '잠긴 내선만 해제할 수 있습니다'),
    release,
  };
}

/**
 * check-sync 응답 처리. 응답은 항상 200 이고 분기는 reason 이다. 통화 중이면 강제 전송
 * 확인이 필요하다는 신호(`confirmForce`)를 돌려준다 — force 는 사용자가 명시했을 때만.
 */
export function checkSyncOutcome(res) {
  if (res.reason === 'EXTENSION_IN_CALL' && !res.notified) {
    return { kind: 'confirmForce', text: '통화 중입니다. 강제로 보낼까요? 전화기가 재시작될 수 있습니다.' };
  }
  if (res.reason === 'EXTENSION_NOT_REGISTERED') {
    return {
      kind: 'info',
      text: '전화기가 등록돼 있지 않아 지금은 보낼 수 없습니다. 전화기가 다음 설정 수신 때 받아 갑니다.',
    };
  }
  const n = res.contacts == null ? '' : `(연결 ${res.contacts}대)`;
  return {
    kind: 'info',
    text: `전화기에 설정 재수신을 요청했습니다${n}. 적용 여부는 전화기(등록) 배지로 확인하세요.`,
  };
}

/**
 * MAC 입력 정규화 — 구분자(: - . 공백)를 빼고 소문자로. 12 hex 가 아니면 null.
 * 서버 계약은 12 hex 소문자다.
 */
export function normalizeMac(input) {
  const v = input.replace(/[\s:.-]/g, '').toLowerCase();
  return /^[0-9a-f]{12}$/.test(v) ? v : null;
}

/** 모든 `_at`(unix ms) → 로컬 `YYYY-MM-DD HH:mm:ss`. 없으면 `—` */
export function formatPbxTime(ms) {
  if (ms == null) return '—';
  return dayjs(ms).format('YYYY-MM-DD HH:mm:ss');
}

/** 상대 시각 — 툴팁 전용("3분 전") */
export function formatPbxRelative(ms, now) {
  if (ms == null) return '';
  const sec = Math.max(0, Math.round((now - ms) / 1000));
  if (sec < 60) return '방금 전';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  return `${Math.floor(hr / 24)}일 전`;
}

/**
 * 내선 탭 "전화기(등록)" 툴팁 — 필드가 없으면(4단계 응답) 그 줄을 뺀다.
 * `상태 변경`(observed_at)과 `마지막 확인`(checked_at)은 다른 시각이다: 잘 붙어 있는 전화기는
 * 상태 변경이 며칠 전일 수 있다.
 */
export function registrationTooltipLines(ext) {
  const r = ext.registration;
  const out = [];
  if (r?.transport) out.push(`전송 ${r.transport.toUpperCase()}`);
  if (r?.contact_host_masked) out.push(`접속 위치 ${r.contact_host_masked}`);
  if (r?.user_agent) out.push(`UA ${r.user_agent}`);
  if (r?.rtt_ms != null) out.push(`RTT ${r.rtt_ms} ms`);
  if (r?.observed_at != null) out.push(`상태 변경 ${formatPbxTime(r.observed_at)}`);
  if (r?.source) out.push(`원천 ${r.source}`);
  if (ext.checked_at != null) out.push(`마지막 확인 ${formatPbxTime(ext.checked_at)}`);
  return out;
}

/** 상담사 번호 탭 등록 배지 툴팁에 덧붙일 7단계 줄(필드 없으면 생략) */
export function summaryTooltipLines(summary) {
  const out = [];
  if (summary.transport) out.push(`전송 ${summary.transport.toUpperCase()}`);
  if (summary.checked_at != null) out.push(`마지막 확인 ${formatPbxTime(summary.checked_at)}`);
  return out;
}

// ---------------------------------------------------------------------------
// admin 전용 보조 — studio 의 `telephoneDisplay.formatTelephoneNumber`·`fileUtils.formatFileSize`
// 를 이 화면이 쓰는 만큼만 옮긴다(이식 계약 §7).
// ---------------------------------------------------------------------------

/**
 * 전화번호 표시. 문자열이 아니면 `''`. 숫자만 추려 3~4자리(내선)면 그대로,
 * 국내 번호(02·0XX·010, 1588 류 대표번호)면 하이픈을 넣고, 그 밖은 입력 그대로 돌려준다.
 */
export function formatTelephoneNumber(value) {
  if (typeof value !== 'string') return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 3 || digits.length === 4) return digits;
  // 서울 02: 02-XXX-XXXX(9자리) / 02-XXXX-XXXX(10자리)
  if (/^02\d{7,8}$/.test(digits)) {
    return digits.length === 9
      ? `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`
      : `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  // 그 외 국번(010·031·070·080 등): 0XX-XXX-XXXX(10자리) / 0XX-XXXX-XXXX(11자리)
  if (/^0\d{9,10}$/.test(digits)) {
    return digits.length === 10
      ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
      : `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  // 대표번호(1588·1577·1600·1644·1661·1666·1670·1688·1800 류): 1XXX-XXXX
  if (/^1\d{7}$/.test(digits)) {
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }
  return value;
}

/** 파일 크기 표시 — `0 B`, `1.2 KB`, `3.4 MB`, `1.0 GB`(KB 이상은 소수 1자리, KB 미만은 정수) */
export function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${Math.floor(n)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

// ---------------------------------------------------------------------------
// 목록 정렬 — 서버는 최신순으로 주지만 화면에서는 번호·이름순이 찾기 쉽다(admin 결정 2026-09-28)
// ---------------------------------------------------------------------------

/**
 * 내선 번호 비교 — 숫자면 숫자 크기순, 아니면 문자열순. 같은 번호는 원래 순서(안정 정렬)를 지킨다.
 * @param {{ number?: string | null }} a
 * @param {{ number?: string | null }} b
 * @returns {number}
 */
export function compareExtensionNumber(a, b) {
  const an = String(a?.number ?? '');
  const bn = String(b?.number ?? '');
  const ai = /^\d+$/.test(an) ? Number(an) : null;
  const bi = /^\d+$/.test(bn) ? Number(bn) : null;
  if (ai !== null && bi !== null) return ai - bi;
  if (ai !== null) return -1;
  if (bi !== null) return 1;
  return an.localeCompare(bn, 'ko');
}

/**
 * 이름(라벨)순 비교 — 한글·숫자 자연 정렬. 이름이 없으면 뒤로.
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @returns {number}
 */
export function compareLabel(a, b) {
  const as = (a ?? '').trim();
  const bs = (b ?? '').trim();
  if (!as && !bs) return 0;
  if (!as) return 1;
  if (!bs) return -1;
  return as.localeCompare(bs, 'ko', { numeric: true, sensitivity: 'base' });
}
